import { Plus, Trash2 } from 'lucide-react';
import type { TrainingSection } from '@hcc/types';
import { Button } from '../../ui/Button';
import { SHARED_INPUT_CLASS } from './pageMeta';

/**
 * Define the module's sections: a title and an optional pass mark each.
 * Pages join a section from their own editor. A section with a pass mark is a
 * gate (the learner must clear it as well as the overall mark); one without
 * is just grouping.
 */
export function SectionManager({
  sections,
  onAdd,
  onUpdate,
  onRemove,
}: {
  sections: TrainingSection[];
  onAdd: () => void;
  onUpdate: (id: string, patch: Partial<Omit<TrainingSection, 'id'>>) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <div className="rounded-token border border-line/10 bg-surface p-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-fg-muted">Sections</span>
        <Button variant="ghost" size="sm" iconLeft={<Plus className="h-4 w-4" />} onClick={onAdd}>
          Add section
        </Button>
      </div>
      {sections.length === 0 ? (
        <p className="mt-1 text-xs text-fg-dim">
          Optional. Group pages and give a section its own pass mark; learners must clear every
          gated section as well as the overall pass mark.
        </p>
      ) : (
        <ul className="mt-2 space-y-2">
          {sections.map((sec) => {
            const gated = sec.pass_mark != null;
            return (
              <li key={sec.id} className="flex flex-wrap items-end gap-2">
                <label className="min-w-40 flex-1 text-sm">
                  <span className="font-medium text-fg-muted">Section title</span>
                  <input
                    value={sec.title}
                    onChange={(e) => onUpdate(sec.id, { title: e.target.value })}
                    className={SHARED_INPUT_CLASS}
                  />
                </label>
                <label className="flex items-center gap-1.5 pb-2 text-sm text-fg">
                  <input
                    type="checkbox"
                    checked={gated}
                    onChange={(e) =>
                      onUpdate(sec.id, { pass_mark: e.target.checked ? 80 : undefined })
                    }
                  />
                  <span>Pass mark</span>
                </label>
                {gated ? (
                  <label className="w-24 text-sm">
                    <span className="font-medium text-fg-muted">%</span>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={sec.pass_mark}
                      onChange={(e) =>
                        onUpdate(sec.id, {
                          pass_mark: Math.min(
                            100,
                            Math.max(0, Math.floor(Number(e.target.value) || 0)),
                          ),
                        })
                      }
                      className={SHARED_INPUT_CLASS}
                    />
                  </label>
                ) : null}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemove(sec.id)}
                  aria-label="Remove section"
                  className="pb-2"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
